import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import searchUI from "@/apis/searchUIClient";
import FilterList from "@cleanup/components/AdvancedFilters/FilterList";
import SimpleFilterList from "@cleanup/components/SimpleFilterList";
import { FilterLineLoader } from "@cleanup/components/AdvancedFilters/FilterStyle";
import { useOrg } from "@/contexts/OrgContext";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasFeature } from "@/hooks/useFeatures";
import { hasPermission } from "@/hooks/usePermissions";

// FilterList types
interface FilterTypeSettings {
  [key: string]: unknown;
}

interface SearchField {
  _idx: string;
  key: string;
  label?: string;
  description: string;
  filterType: string;
  filterTypeSettings: FilterTypeSettings;
  [key: string]: unknown;
}

interface CategoryDescriptor {
  categoryID: string;
  categoryLabel: string;
  searchFields: SearchField[];
}

interface FilterParam {
  filterKey: string;
  filterType: string;
  categoryId?: string;
  searchType?: string;
  filterValue?: Record<string, unknown>;
  notFilter?: boolean;
  [key: string]: unknown;
}

interface BasicQuery {
  searchQueryType?: string;
  query: FilterParam[];
}

interface Action {
  type: string;
  idx: string | null;
}

interface ForceFilterAction {
  filterKeys: string[];
  action: string;
  query?: (string | number | boolean | null)[];
}

interface ActionToolOption {
  id: string;
  type?: string;
  title?: string;
  icon?: React.ReactNode;
  link?: string;
  fullLinkInNewTab?: string;
  customActiveClass?: string;
  isHideText?: boolean;
  isAlwayShow?: boolean;
  disabled?: boolean;
  disabledTitle?: string;
}

interface FilterListData {
  queryObjectType: string;
  descriptors: CategoryDescriptor[];
  appliedFilterSettings?: FilterParam[];
  filtersVisibleAtInit?: string[];
}

interface OrgContextType {
  orgId: number | null;
  updateOrg: (newOrgId: number | null) => void;
}



interface DataOptions {
  queryObjectType?: string;
  categories?: string[];
  optionsSettings?: {
    query?: FilterParam[];
  };
  filtersVisibleAtInit?: string[];
  deviceCategorySuggestion?: string;
  filter?: {
    search?: string;
  };
}


interface ToolbarHeaderProps {
  dataOptions?: DataOptions;
  children?: () => React.ReactNode;
  onChange?: (data: BasicQuery, action?: Action) => void;
  restrictedFilters?: string[];
  isAdvanceFilter?: boolean;
  totalText?: string;
  onChangeSimpleFilterList?: (data: { search: string }) => void;
  filterToolList?: ActionToolOption[];
  handleTool?: (tool: unknown) => void;
  searchPlaceholder?: string;
  isHideTheUI?: boolean;
  filterListRef?: React.MutableRefObject<{ clearFilter: () => void } | undefined>;
  disabled?: boolean;
  onClearAllButtonClicked?: () => void;
  includeDynamicFilter?: boolean;
  forceFilterAction?: ForceFilterAction | null;
  setForceFilterAction?: (action: ForceFilterAction | null) => void;
  onLoadFilter?: (filtersMap: Record<string, string>) => void;
}

const ToolbarHeader: React.FC<ToolbarHeaderProps> = ({
  dataOptions = {},
  children,
  onChange,
  restrictedFilters,
  isAdvanceFilter = true,
  totalText,
  onChangeSimpleFilterList,
  filterToolList,
  handleTool,
  searchPlaceholder,
  isHideTheUI = false,
  filterListRef,
  disabled = false,
  onClearAllButtonClicked,
  includeDynamicFilter = true,
  forceFilterAction,
  setForceFilterAction,
  onLoadFilter,
}) => {
  const optionsSettings = useRef<{ query?: FilterParam[] } | null>(null);
  const descriptors = useRef<CategoryDescriptor[] | null>(null);

  const [filterListData, setFilterListData] = useState<FilterListData | null>(null);
  const orgContext = useOrg() as OrgContextType | null;
  const orgId = orgContext?.orgId;

  const loadBasicComponents = () => {
    if (isAdvanceFilter) {
      let _categoriesParam = null;
      if (dataOptions.categories) {
        _categoriesParam = { categories: dataOptions.categories };
      }
      const orgParam = orgId && !disabled ? `?org=${orgId}` : "";
      searchUI
        .getDescriptors(dataOptions.queryObjectType, _categoriesParam, orgParam, null)
        .then(({ data: _descriptors }: { data: CategoryDescriptor[] }) => {
          if (includeDynamicFilter === false) {
            _descriptors.forEach(
              (category) =>
                (category.searchFields = category.searchFields.filter(
                  (filter) => filter.searchType !== "DYNAMIC"
                ))
            );
          }
          descriptors.current = _descriptors;

          const filtersMap: Record<string, string> = {};
          _descriptors.forEach((item) => {
            item.searchFields.forEach((filter) => {
              filtersMap[filter.key] = filter.filterType;
            });
          });
          if (onLoadFilter) {
            onLoadFilter(filtersMap);
          }

          setFilterListData({
            queryObjectType: dataOptions.queryObjectType || "",
            descriptors: _descriptors,
            appliedFilterSettings:
              dataOptions &&
              dataOptions.optionsSettings &&
              dataOptions.optionsSettings.query
                ? dataOptions.optionsSettings.query
                : [],
            filtersVisibleAtInit:
              !hasFeature(FeatureKey.search_advanced) &&
              !hasPermission(UserPermission.SYSTEM_ADMIN_VIEW)
                ? ["deviceIdentifier", "assetTags", "trackerStatus"]
                : dataOptions.filtersVisibleAtInit,
          });
        })
        .catch((error) => {
          console.log("can not load descriptors: ", error);
        });
    }
  };

  const onFilterChange = (data: BasicQuery, action?: Action) => {
    if (onChange) {
      onChange(
        {
          searchQueryType: data.searchQueryType,
          query: data.query,
        },
        action
      );
    }
    optionsSettings.current = { ...optionsSettings.current, query: data.query };
  };

  const onResetFilterData = () => {
    if (filterListData && optionsSettings.current?.query) {
      setFilterListData({
        ...filterListData,
        appliedFilterSettings: optionsSettings.current.query.map((query) => {
          delete query.filterValue;
          query.notFilter = false;
          return query;
        }),
      });
    }
    if (onClearAllButtonClicked) {
      onClearAllButtonClicked();
    }
  };

  useEffect(() => {
    if (isAdvanceFilter) {
      optionsSettings.current = dataOptions.optionsSettings || null;

      if (
        !filterListData ||
        filterListData.queryObjectType !== dataOptions.queryObjectType
      ) {
        loadBasicComponents();
      } else {
        setFilterListData({
          queryObjectType: dataOptions.queryObjectType || "",
          descriptors: descriptors.current || [],
          appliedFilterSettings:
            dataOptions &&
            dataOptions.optionsSettings &&
            dataOptions.optionsSettings.query
              ? dataOptions.optionsSettings.query
              : [],
          filtersVisibleAtInit:
            !hasFeature(FeatureKey.search_advanced) && !hasPermission(UserPermission.SYSTEM_ADMIN_VIEW)
              ? ["deviceIdentifier", "assetTags", "trackerStatus"]
              : dataOptions.filtersVisibleAtInit,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataOptions]);

  const handleSimpleFilterList = (data: { search: string }) => {
    if (onChangeSimpleFilterList) {
      onChangeSimpleFilterList(data);
    }
  };

  return (
    <>
      <Row style={{ display: isHideTheUI ? "none" : "" }}>
        <Col>
          {isAdvanceFilter ? (
            filterListData ? (
              <>
                <FilterList
                  restrictedFilters={restrictedFilters}
                  data={filterListData}
                  isVisibleAddFilterBtn={
                    hasFeature(FeatureKey.alert_search) || hasFeature(FeatureKey.search_advanced)
                  }
                  onChange={onFilterChange}
                  onResetFilterData={onResetFilterData}
                  filterListRef={filterListRef}
                  disabled={disabled}
                  totalElement={totalText}
                  filterToolList={filterToolList}
                  handleTool={handleTool}
                  deviceCategorySuggestion={dataOptions.deviceCategorySuggestion}
                  forceFilterAction={forceFilterAction}
                  setForceFilterAction={setForceFilterAction}
                  isDisableCollapseOnMobile={undefined}
                />
              </>
            ) : (
              <FilterLineLoader></FilterLineLoader>
            )
          ) : (
            <SimpleFilterList
              onChange={handleSimpleFilterList}
              data={dataOptions}
              multiMode={undefined}
              searchPlaceholder={searchPlaceholder}
            />
          )}
        </Col>
      </Row>

      <div id="table-page-content">{children && children()}</div>
    </>
  );
};

export default ToolbarHeader;
